import { useIdentity } from '$api-clients/hooks';

/**
 * Organization IDs owned by Side, one per environment (test/stage/prod). Org
 * IDs are globally unique UUIDs, so listing every environment lets the check
 * work anywhere without detecting which one we're running in — an org whose ID
 * isn't one of these belongs to an external (BYOB) brokerage.
 */
const SIDE_ORGANIZATION_IDS = new Set<string>([
  '8f11bb43-b258-4ae1-a342-eeb06a20566b', // test
  '04d7b05b-f0b9-4caf-85cc-4f3e8d6d41e6', // stage
  'b020b5ec-cb29-4af9-81f5-4bc4174c7673', // prod
]);

/**
 * Whether the authenticated user's brokerage has an external (non-Side) broker
 * of record — i.e. a "Bring Your Own Brokerage" (BYOB) org.
 *
 * Derived from the organizations on the identity `/auth/userinfo` response: the
 * broker is external when the user belongs to at least one organization that is
 * not a known Side org. Matching on org IDs is a stopgap; longer term this will
 * likely be driven by organization settings.
 *
 * `isExternal` fails safe to external until userinfo returns data — so while the
 * request is loading, or if it errors, we show the BYOB experience rather than
 * briefly flashing (or, on a failed request, exposing) Side-only features. A
 * successful response with no external org is the only path to internal.
 * Consumers that need to distinguish "still resolving" from "resolved external"
 * — e.g. a route guard that shouldn't flash an error page to internal brokers —
 * can read `isLoading`.
 *
 * @see https://residenetwork.atlassian.net/wiki/spaces/SAR/pages/4664655893
 */
export const useBrokerIsExternal = (): {
  isExternal: boolean;
  isLoading: boolean;
} => {
  const { data, isLoading } = useIdentity('/auth/userinfo');

  const isExternal =
    data === undefined ||
    (data.organizations ?? []).some(
      (organization) => !SIDE_ORGANIZATION_IDS.has(organization.id),
    );

  return { isExternal, isLoading };
};
